<template>
  <div class="content-wrapper step-2">
    <div class="step-wrapper">
      <div class="step">Etape 3/3</div>
      <div class="form-wrapper">
        <p class="explanations">
          La carte ci-dessous représentera votre campagne dans l’espace des
          talents.<br />
          Après l'avoir renseignée, vous pouvez aller plus loin en la
          personnalisant avec l’icône double flèche : cette fonctionnalité vous
          permet de modifier les visuels et les couleurs de la campagne,
          sublimant ainsi votre marque.
        </p>
        <div class="campaign-layout">
          <CampaignCard
            data-step="18"
            :data="state.publicCampaignData"
            @changeImage="prepareUploadHeaderImage"
            @changePublicName="
              (e) => (state.publicCampaignData.public_name = e)
            "
            @changeJobTitle="(e) => (state.publicCampaignData.job_title = e)"
            @changeJobDescription="
              (e) => (state.publicCampaignData.job_description = e)
            "
            @toggleError="(val) => (state.publicCampaignCardError = val)"
            :mode="campaignCardMode"
            :text_colour="state.publicCampaignData.text_colour"
            :elem_colour="state.publicCampaignData.elem_colour"
          />
          <CampaignThemeCard
            ref="campaignThemeRef"
            @toggleTheme="toggleTooltip"
            @changeTextColour="
              (e) => (state.publicCampaignData.text_colour = e)
            "
            @changeThemeColour="
              (e) => (state.publicCampaignData.elem_colour = e)
            "
            @setHeaderImage="handleHeaderImage"
            @setBackground="handleBGSelect"
            @newBackground="handleBGUploaded"
          />
        </div>
        <div class="buttons-inline">
          <Button
            class="secondary icon icon-left no-dark-mode campaign-button"
            label="Retourner à la création"
            icon="bi-chevron-left"
            @click="goBack()"
          />
          <Button
            class="icon icon-left no-dark-mode campaign-button campaign-validate"
            :disabled="!canGoNext"
            :loading="state.isLoading"
            label="Valider votre campagne"
            icon="bi-check-lg"
            @click="createNewCampaign()"
            v-tooltip="{
              content: 'Tous les champs ne sont pas remplis.',
              triggers: canGoNext ? [] : ['hover'],
            }"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, reactive, ref } from "vue";
import { useCampaignStore } from "@/stores/campaign.js";
import { useRoute, useRouter, onBeforeRouteLeave } from "vue-router";
import { useToast } from "vue-toastification";
import { useAppStore } from "@/stores/app.js";
import { useImageStore } from "@/stores/image.js";
import { getError } from "@/composables/error.js";
import { getFileFromURL, getImage } from "@/composables/getImage.js";

const appStore = useAppStore();

const toast = useToast();

// Init stores
const { uploadImage } = useImageStore();
const { createCampaign } = useCampaignStore();

const route = useRoute();

const router = useRouter();

const emits = defineEmits(["prev", "handleBGUpdate"]);

const props = defineProps({
  campaignData: {
    type: Object,
    required: true,
  },
});

const campaignCardMode = ref("new");

const campaignThemeRef = ref(null);

const state = reactive({
  publicCampaignData: {
    name: props.campaignData.name,
    public_name: null,
    job_title: null,
    job_description: null,
    image_id: null,
    bg_image_id: null,
    text_colour: null,
    elem_colour: null,
    calibration_id: props.campaignData.calibration_id,
  },
  isLoading: false,
  campaign_image: null,
  campaign_bg_image: null,
  publicCampaignCardError: false,
  mode: "new",
});

const canGoNext = computed(() => {
  if (state.publicCampaignCardError) return false;

  const { public_name, job_title, job_description } = state.publicCampaignData;

  return public_name && job_title && job_description;
});

function goBack() {
  handleBGSelect({ image: false, toUpload: false });
  emits("prev");
}

async function createNewCampaign() {
  state.isLoading = true;

  if (state.campaign_image) {
    const { image_id } = await uploadImage(
      route.params.companyId,
      "campaign_public_image",
      state.campaign_image
    );

    if (image_id) {
      state.image_id = image_id;
    } else {
      toast.error("Une erreur est survenue lors du téléchargement de l'image");
    }
  }

  if (state.campaign_bg_image) {
    const { image_id } = await uploadImage(
      route.params.companyId,
      "campaign_public_bg_image",
      state.campaign_bg_image
    );

    if (image_id) {
      state.bg_image_id = image_id;
    } else {
      toast.error("Une erreur est survenue lors du téléchargement de l'image");
    }
  }

  const resp = await createCampaign(
    route.params.companyId,
    props.campaignData.workspace_id,
    {
      name: props.campaignData.name,
      public_name: state.publicCampaignData.public_name,
      job_title: state.publicCampaignData.job_title,
      job_description: state.publicCampaignData.job_description,
      image_id: state.image_id ? state.image_id : null,
      text_colour: state.publicCampaignData.text_colour,
      elem_colour: state.publicCampaignData.elem_colour,
      bg_image_id: state.bg_image_id ? state.bg_image_id : null,
      skills_mandatory: props.campaignData.skills_mandatory,
      skills_important: props.campaignData.skills_important,
      skills_secondary: props.campaignData.skills_secondary,
      calibration_id: props.campaignData.calibration_id,
      version: 2,
    }
  );

  if (!resp.code) {
    router.push({
      name: "campaign",
      params: {
        spaceId: resp.workspace_id,
        campaignId: resp.campaign_id,
      },
    });

    toast.success("Nouvelle campagne créée avec succès");
  } else {
    toast.error(getError(resp.key));
  }

  state.isLoading = false;
}

function toggleTooltip(value) {
  if (value) {
    campaignCardMode.value = "new";
  } else {
    campaignCardMode.value = "hide";
  }
}

async function handleBGSelect(data) {
  const imageBGUrl = data.image;
  const toUpload = data.toUpload;
  emits("handleBGUpdate", imageBGUrl); // Re-emit to parent
  if (toUpload) {
    try {
      state.campaign_bg_image = await getFileFromURL(
        imageBGUrl,
        "image.jpg",
        "image/jpeg"
      );
    } catch (error) {
      console.error("Error converting URL to file:", error);
    }
  } else {
    //remove previous image if there was one
    state.campaign_bg_image = null;
  }
}

// Pass the file uploaded event to the parent component
function handleBGUploaded(imgFile) {
  if (imgFile) {
    const imageBGUrl = URL.createObjectURL(imgFile);
    emits("handleBGUpdate", imageBGUrl); // Re-emit to parent
    state.campaign_bg_image = imgFile;
  } else {
    emits("handleBGUpdate", false); // Re-emit to parent
    state.campaign_bg_image = null;
  }
}

async function handleHeaderImage(data) {
  const imageId = data.image;
  state.publicCampaignData.image_id = imageId;
  if (imageId) {
    const imageUrl = getImage(imageId);
    try {
      state.campaign_image = await getFileFromURL(
        imageUrl,
        "image.jpg",
        "image/jpeg"
      );
    } catch (error) {
      console.error("Error converting URL to file:", error);
    }
  } else {
    state.campaign_image = null;
  }
}

function prepareUploadHeaderImage(imgFile) {
  state.campaign_image = imgFile;
  // Reset selection on changing header
  if (campaignThemeRef.value && campaignThemeRef.value.resetSelection) {
    campaignThemeRef.value.resetSelection(); // Call the method on the child component
  }
}

if (!appStore.isOnboarded) {
  onBeforeRouteLeave((to, from, next) => {
    if (to.name !== "campaign" && appStore.chapterState.isComplete) {
      window.confirm("Souhaitez-vous vraiment quitter l'onboarding ?")
        ? (next(), (appStore.isOnboarded = true))
        : null;
    } else {
      next();
    }
  });
}

onMounted(async () => {
  if (!appStore.isOnboarded) {
    const newCampaignModule = await import("@/onboarding/newCampaign-2.js");

    setTimeout(() => {
      appStore.startChapter(newCampaignModule.default);
    }, 300);
  }
});
</script>
